import { useCallback, useMemo, useState } from "react";
import type { QueueSummary } from "../../../experience-shell/queues/types";
import { splitCandidates, type RecipientCandidates } from "./recipients";

/**
 * Filtered recipient candidates plus the highlighted row.
 *
 * Results are computed synchronously from the in-memory tree, so the first
 * frame of the list is complete. The highlight is stored with the query it
 * belongs to and resets to the first row when the query changes, without an
 * effect and without an intermediate frame.
 */

export interface RecipientSearch {
  readonly candidates: RecipientCandidates;
  /** People then queues, in display order. */
  readonly flat: readonly QueueSummary[];
  readonly highlighted: QueueSummary | undefined;
  readonly move: (delta: number) => void;
  readonly highlightId: (queueId: string) => void;
}

interface HighlightState {
  readonly query: string;
  readonly index: number;
}

export function useRecipientSearch(
  allWorkable: readonly QueueSummary[],
  selectedIds: readonly string[],
  excludeIds: readonly string[] | undefined,
  query: string,
): RecipientSearch {
  const candidates = useMemo(
    () => splitCandidates(allWorkable, selectedIds, excludeIds, query),
    [allWorkable, selectedIds, excludeIds, query],
  );
  const flat = useMemo(
    () => [...candidates.people, ...candidates.queues],
    [candidates.people, candidates.queues],
  );

  const [highlight, setHighlight] = useState<HighlightState>({ query, index: 0 });
  const rawIndex = highlight.query === query ? highlight.index : 0;
  const index = flat.length === 0 ? -1 : Math.min(rawIndex, flat.length - 1);

  const move = useCallback(
    (delta: number) => {
      if (flat.length === 0) return;
      const next = (index + delta + flat.length) % flat.length;
      setHighlight({ query, index: next });
    },
    [flat.length, index, query],
  );

  const highlightId = useCallback(
    (queueId: string) => {
      const next = flat.findIndex((q) => q.id === queueId);
      if (next >= 0) setHighlight({ query, index: next });
    },
    [flat, query],
  );

  return {
    candidates,
    flat,
    highlighted: index >= 0 ? flat[index] : undefined,
    move,
    highlightId,
  };
}
